/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageSEO } from '@/shared/seo';
import { Container, Title, Text, Stepper, Paper, Stack } from '@/shared/ui';
import { useAuth } from '@context/authentication';
import { DataRightType } from '../types/rights-types';
import { RightsTypeSelector } from '../components/RightsTypeSelector';
import { RightsRequestForm } from '../components/RightsRequestForm';
import { RequestConfirmation } from '@/shared/ui/components/composed/data-rights/RequestConfirmation';

type Step = 'select' | 'form' | 'confirmation';

/**
 * DataRightsPage
 * Main page for data subject rights requests (LGPD Art. 18)
 */
export const DataRightsPage: React.FC = () => {
  const { t } = useTranslation('@pages/data-rights');
  const { user } = useAuth();
  
  const [currentStep, setCurrentStep] = useState<Step>('select');
  const [selectedType, setSelectedType] = useState<DataRightType | null>(null);
  const [protocolNumber, setProtocolNumber] = useState<string>('');

  const handleSelectType = (type: DataRightType) => {
    setSelectedType(type);
    setCurrentStep('form');
  };

  const handleSubmit = async (description: string) => {
    // Generate protocol number
    const protocol = `DPO-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
    
    // In a real implementation, this would:
    // 1. Save to database (data_rights_requests table)
    // 2. Send email to privacy@k4d.com.br
    // 3. Send confirmation email to user
    
    // For now, we'll simulate the API call
    await new Promise((resolve) => setTimeout(resolve, 1000));
    
    // Simulate email sending
    console.log('Data Rights Request:', {
      protocol,
      userId: user?.profile?.sub,
      userEmail: user?.profile?.email,
      userName: user?.profile?.name,
      requestType: selectedType,
      description,
      timestamp: new Date().toISOString(),
    });

    setProtocolNumber(protocol);
    setCurrentStep('confirmation');
  };

  const handleCancel = () => {
    setCurrentStep('select');
    setSelectedType(null);
  };

  const handleBackToHome = () => {
    // Navigate back to settings
    window.location.href = '/app/settings';
  };

  const getStepIndex = () => {
    switch (currentStep) {
      case 'select':
        return 0;
      case 'form':
        return 1;
      case 'confirmation':
        return 2;
      default:
        return 0;
    }
  };

  return (
    <Container size="md" className="py-(--space-12)">
      <PageSEO title={t("pageTitle")} />
      <Stack gap="xl">
        <div>
          <Title order={1} className="mb-(--space-2)">
            {t('dataRights.title')}
          </Title>
          <Text className="text-(--text-secondary)">{t('dataRights.description')}</Text>
        </div>

        <Stepper active={getStepIndex()} breakpoint="sm">
          <Stepper.Step label={t('dataRights.steps.select')} description={t('dataRights.steps.selectDesc')} />
          <Stepper.Step label={t('dataRights.steps.details')} description={t('dataRights.steps.detailsDesc')} />
          <Stepper.Step label={t('dataRights.steps.confirmation')} description={t('dataRights.steps.confirmationDesc')} />
        </Stepper>

        <Paper className="shadow-[var(--shadow-sm)] p-(--space-6) rounded-(--radius-lg) border border-(--border-subtle)">
          {currentStep === 'select' && (
            <RightsTypeSelector selectedType={selectedType} onSelect={handleSelectType} />
          )}

          {currentStep === 'form' && selectedType && (
            <RightsRequestForm
              selectedType={selectedType}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
            />
          )}

          {currentStep === 'confirmation' && (
            <RequestConfirmation
              protocolNumber={protocolNumber}
              userEmail={user?.profile?.email || ''}
              onBackToHome={handleBackToHome}
            />
          )}
        </Paper>

        <Text size="xs" className="text-(--text-secondary)" ta="center">
          {t('dataRights.footer')}
        </Text>
      </Stack>
    </Container>
  );
};
